import { StatusCategory, Color as ColorApi } from '@cycle-app/graphql-codegen';
import { rgba, opacify, transparentize, lighten, darken } from 'polished';

import { ColorNuance } from '../../types/nuance.types';
import { DeepPartial } from '../../types/partial.types';
import { Color, LegacyColor } from '../baseColors';

const text = {
  primary: Color.Black,
  secondary: Color.Grey700,
  opposite: Color.White,
  disabled: Color.Grey500,
  highlight: Color.Black,

  white: Color.White,
  black: Color.Black,
  blue: Color.Blue600,
  green: Color.Green700,
  yellow: Color.Yellow600,
  orange: Color.Orange600,
  orangered: Color.OrangeRed600,
  red: Color.Red600,
  pink: Color.Pink600,
  purple: Color.Purple600,
  violet: Color.Violet600,
};

const background = {
  primary: Color.White,
  secondary: Color.Grey100,
  tertiary: Color.Grey200,
  active: Color.Grey400,
  opposite: Color.Black,
  hover: Color.Grey300,
  hoverSoft: Color.Grey100,
  hoverDark: Color.Grey800,

  transparent: 'transparent',
  black: Color.Black,
  white: Color.White,
  grey: Color.Grey100,
  disabled: Color.Grey300,
  highlight: Color.Yellow300,

  blue: Color.Blue600,
  bluelight: rgba(Color.Blue600, 0.1),
  blueDarker: Color.Blue700,
  green: Color.Green700,
  greenlight: rgba(Color.Green700, 0.1),
  yellow: Color.Yellow600,
  yellowlight: rgba(Color.Yellow600, 0.1),
  orange: Color.Orange600,
  orangelight: rgba(Color.Orange600, 0.1),
  orangered: Color.OrangeRed600,
  orangeredlight: rgba(Color.OrangeRed600, 0.1),
  red: Color.Red600,
  redlight: rgba(Color.Red600, 0.1),
  pink: Color.Pink600,
  pinklight: rgba(Color.Pink600, 0.1),
  purple: Color.Purple600,
  purplelight: rgba(Color.Purple600, 0.1),
  violet: Color.Violet600,
  violetlight: rgba(Color.Violet600, 0.1),
  mauve: Color.Violet500,
};

const border = {
  primary: Color.Grey300,
  secondary: Color.Grey100,
  hover: Color.Grey200,
  hoverDarker: Color.Grey500,
  transparent: 'transparent',
  input: Color.Grey400,

  greyLight: Color.Grey300,
  grey: Color.Grey400,
  greyDark: Color.Grey800,

  white: Color.White,
  blue: Color.Blue600,
  green: Color.Green700,
  yellow: Color.Yellow600,
  orange: Color.Orange600,
  orangered: Color.OrangeRed600,
  red: Color.Red400,
  pink: Color.Pink600,
  purple: Color.Purple600,
  violet: Color.Violet600,

  surprisingGrey: rgba(Color.Black, 0.1),
  surprisingGreyDark: rgba(Color.Black, 0.05),
};

// As default theme — eclipse should stay implicitly typed
export const colors = {
  text,
  background,
  border,
  authLayout: {
    content: {
      bg: background.primary,
      color: Color.Black,
    },
    sidebar: {
      bg: LegacyColor.Beige,
      tooltip: {
        bg: Color.White,
        color: Color.Black,
      },
    },
  },
  avatar: {
    bg: Color.White,
    bgOpposite: Color.Black,
    bgLight: Color.Grey300,
    pending: Color.Grey400,
    pendingLight: Color.Grey300,
  },
  bulkActions: {
    bg: background.primary,
    border: border.primary,
    outline: rgba(0, 0, 0, 0.06),
  },
  button: {
    light: {
      bg: background.secondary,
      bgHover: background.tertiary,
    },
    noColor: {
      bg: background.white,
      color: text.primary,
      border: text.primary,
    },
    secondary: {
      bg: background.transparent,
      bgHover: background.tertiary,
      bgFocus: background.hover,
      bgActive: Color.Grey300,
      color: text.primary,
      enableUserColor: true,
    },
    ternary: {
      color: text.primary,
      bg: background.secondary,
      bgHover: background.tertiary,
      bgActive: background.hoverSoft,
      bgFocus: background.hover,
    },
    outlined: {
      bg: Color.Grey200,
      color: text.disabled,
      bgHover: Color.Grey300,
      colorHover: text.primary,
      bgActive: Color.White,
      colorActive: text.primary,
    },
    warningSecondary: {
      color: text.red,
    },
    secondaryV2: {
      bg: background.transparent,
      bgHover: Color.Grey200,
      bgActive: Color.Grey300,
      color: Color.Grey500,
      colorHover: Color.Grey700,
    },
  },
  body: {
    bg: Color.Grey100,
    color: Color.Black,
  },
  card: {
    bg: Color.White,
    bgHover: Color.Grey100,
  },
  docPanel: {
    bg: background.primary,
    overlay: rgba(0, 0, 0, 0.5),
    header: {
      breadCrumbSeparatorColor: Color.Grey400,
    },
    rightPanel: {
      border: Color.Grey300,
    },
    insightButton: {
      enableUserColor: true,
    },
    hierarchy: {
      bg: background.secondary,
      bgModal: background.secondary,
      bgHover: background.tertiary,
    },
  },
  comments: {
    color: Color.Grey800,
  },
  dropdown: {
    bg: background.primary,
    color: text.primary,
    border: LegacyColor.Grey,
    sep: LegacyColor.Grey200,
    outline: rgba(Color.Black, 0.06),
  },
  dropZone: {
    bg: Color.White,
  },
  editor: {
    text: Color.Grey900,
    textHighlight: rgba(Color.Black, 0.08),
    filePreview: {
      bg: Color.Grey100,
      bgHover: Color.Grey200,
    },
  },
  flowElement: {
    preview: {
      bg: background.primary,
    },
  },
  flowPath: {
    stroke: Color.Grey400,
  },
  input: {
    bg: Color.Grey200,
    bgAlt: Color.White,
    hover: Color.Grey300,
  },
  inputRadio: {
    bg: background.primary,
  },
  group: {
    bg: Color.Grey200,
  },
  modal: {
    border: border.transparent,
    mask: {
      bg: LegacyColor.Black,
    },
    content: {
      bg: background.primary,
      bgNoMask: background.primary,
    },
    icon: {
      warning: {
        bg: background.redlight,
        color: background.red,
      },
    },
  },
  notificationCenter: {
    bg: background.primary,
    color: text.primary,
    border: LegacyColor.Grey,
  },
  userModal: {
    themePreview: {
      bg: Color.Grey400,
      bgHover: Color.Grey400,
    },
  },
  actionButton: {
    color: text.disabled,
    colorHover: text.primary,
    colorDisabled: Color.Grey300,
    bg: background.transparent,
    bgHover: background.tertiary,
    bgActive: background.hover,
    bgHoverDark: Color.Grey300,
    bgActiveDark: Color.Grey400,
    outline: rgba(Color.Black, 0.06),
    warning: {
      bg: null,
      bgHover: background.redlight,
      colorHover: text.red,
      color: null,
    },
    special: {
      bg: null,
      bgHover: null,
      colorHover: text.yellow,
      color: null,
    },
    onColoredBg: {
      color: Color.White,
      bgHover: Color.White,
      colorHover: text.primary,
    },
  },
  skeleton: {
    bg: Color.Grey400,
  },
  sidebar: {
    bg: Color.Grey900,
    color: Color.Grey500,
    border: Color.Transparent,
    colorHover: Color.White,
    separator: rgba(Color.White, 0.1),
    header: {
      bg: Color.Transparent,
      menus: {
        color: Color.White,
        emailColor: Color.Grey500,
      },
      title: {
        color: Color.White,
      },
      item: {
        color: text.disabled,
        bg: Color.Transparent,
        bgHover: Color.Grey850,
        caret: {
          color: Color.Grey500,
          colorHover: Color.White,
        },
      },
      shortcut: {
        bg: Color.Grey800,
        bgHover: Color.Grey850,
        color: Color.Grey500,
      },
    },
    item: {
      bgHover: Color.Grey850,
      bgPlaceholder: Color.Grey900,
      dotsMenu: {
        colorHover: Color.White,
        bgHover: Color.Grey900,
      },
      warning: {
        color: Color.Grey500,
        colorHover: Color.White,
        bgHover: Color.Grey900,
      },
    },
    boardItem: {
      color: Color.White,
    },
    shortcut: {
      bg: Color.Grey850,
      color: Color.White,
      bgHover: Color.Grey800,
      colorHover: Color.Grey500,
      active: {
        bgHover: rgba(Color.Black, 0.1),
        colorHover: Color.White,
      },
    },
    activeItem: {
      color: Color.White,
    },
    droppableArea: {
      bg: Color.Grey800,
    },
    newSection: {
      color: Color.Grey700,
    },
    newSectionInput: {
      bg: Color.Grey900,
      border: Color.Grey800,
      color: Color.White,
    },
    boardsSectionItem: {
      bg: Color.Grey850,
      color: Color.Grey700,
      bgHover: Color.Grey800,
      colorHover: Color.White,
    },
    card: {
      bg: Color.Grey850,
      text: Color.White,
    },
    dropdown: {
      bg: Color.Grey850,
      text: Color.White,
      separator: Color.Grey800,
    },
    freePlan: {
      bg: Color.Grey850,
      text: {
        primary: Color.White,
        secondary: Color.Grey500,
      },
      link: {
        bg: Color.Grey800,
        bgHover: lighten(0.05, Color.Grey800),
        bgActive: lighten(0.1, Color.Grey800),
        text: Color.Grey500,
      },
    },
    freePlanWarning: {
      bg: rgba(Color.Red500, 0.25),
      text: {
        primary: Color.White,
        secondary: Color.Red500,
      },
      link: {
        bg: rgba(Color.Red400, 0.25),
        bgHover: rgba(Color.Red400, 0.35),
        bgActive: rgba(Color.Red400, 0.50),
        text: Color.Red300,
      },
    },
  },
  selectPanel: {
    options: {
      bg: Color.Grey200,
    },
  },
  selectItem: {
    color: text.primary,
    bg: background.transparent,
    bgHover: background.grey,
    bgActive: darken(0.02, background.grey),
    colorDisabled: text.disabled,
  },
  selectItemSpecial: {
    colorHover: LegacyColor.Yellow,
  },
  selectItemDanger: {
    colorHover: Color.Red500,
    bgHover: Color.Red100,
  },
  shortcut: {
    bg: Color.White,
    color: text.primary,
    active: {
      color: text.primary,
      bg: rgba(Color.White, 0.6),
    },
    oposite: {
      bg: rgba(Color.White, 0.5),
      color: LegacyColor.Dark,
    },
    dark: {
      bg: Color.White,
      color: text.primary,
    },
  },
  scrollbar: {
    color: LegacyColor.DirtyTransparentGrey,
    colorHover: LegacyColor.DirtyTransparentGrey,
  },
  tag: {
    border: border.primary,
    bg: Color.Grey100,
    bgHover: Color.Grey200,
    bgActive: Color.Grey300,
  },
  toaster: {
    bg: background.black,
    buttons: {
      bgActive: Color.Grey700,
      bgHover: Color.Grey800,
      bg: Color.Grey850,
    },
  },
  tooltip: {
    bg: Color.Grey900,
    bgAlt: Color.Grey800,
    color: Color.White,
    textAlt: Color.Grey400,
    textLight: Color.Grey500,
    shortcut: {
      bg: Color.Grey800,
      text: Color.Grey400,
    },
  },
  boardConfig: {
    filters: {
      fakeInputBg: background.tertiary,
      element: {
        bg: background.primary,
        bgHover: background.hoverSoft,
        bgFocus: background.hover,
      },
    },
    toolbar: {
      viewTypesBg: background.secondary,
      selectedViewTypeBg: background.primary,
      button: {
        bg: background.secondary,
      },
    },
    preview: {
      swimlane: {
        bg: Color.Blue100,
        border: border.surprisingGrey,
      },
      avatar: {
        bg: Color.Grey300,
        enableUserColor: true,
      },
      group: {
        bg: Color.Grey200,
      },
      card: {
        bg: Color.White,
        title: {
          bg: background.tertiary,
        },
      },
    },
  },
  customProperty: {
    input: {
      bgHover: background.grey,
    },
  },
  buttonWarning: LegacyColor.Red,
  commandbar: {
    color: text.primary,
    bg: rgba(background.primary, 0.88),
    bgFilter: 'blur(25px)',
    outline: '1px solid rgba(0, 0, 0, 0.06)',
    borderFullProp: 'none',
    shadow: `
      0px 0px 120px rgba(43, 43, 43, 0.1),
      0px 34px 64px rgba(43, 43, 43, 0.12),
      0px 0px 32px rgba(43, 43, 43, 0.04),
      0px 24px 24px rgba(43, 43, 43, 0.04),
      0px 4px 24px rgba(43, 43, 43, 0.04),
      0px 4px 4px rgba(43, 43, 43, 0.04)
    `,
    category: {
      color: text.disabled,
      bg: background.primary,
    },
    input: {
      bg: LegacyColor.Transparent,
      color: text.primary,
      placeholder: transparentize(0.7, text.primary),
      icon: transparentize(0.7, text.primary),
    },
    action: {
      color: Color.Grey800,
      colorHover: Color.Grey800,
      bgHover: background.tertiary,
    },
    actionIcon: {
      color: text.disabled,
    },
    tag: {
      bg: background.tertiary,
      color: text.primary,
    },
    shortCut: {
      color: text.disabled,
      bg: background.disabled,
    },
  },
  settings: {
    bg: background.primary,
    navigationItem: {
      bgHover: background.hover,
      draggingBg: rgba(Color.White, 0.94),
      draggingColor: Color.Black,
      draggingBorderColor: Color.Grey300,
      menu: {
        bgHover: Color.White,
      },
    },
    row: {
      bgHover: background.secondary,
    },
    sidebar: {
      grab: {
        color: Color.Grey700,
      },
    },
  },
  workflows: {
    status: {
      bg: Color.Grey100,
      bgHover: Color.Grey200,
    },
    button: {
      color: Color.Grey500,
      colorHover: Color.Grey700,
      bgHover: Color.Grey200,
      bgActive: Color.Grey300,
    },
    buttonAlt: {
      color: Color.Grey500,
      colorHover: Color.Grey700,
      bgHover: Color.Grey300,
      bgActive: darken(0.05, Color.Grey300),
    },
  },
  settingsModal: {
    leftPanel: {
      bg: background.secondary,
    },
    rightPanel: {
      bg: background.primary,
      appearance: {
        selectionColor: Color.Blue500,
      },
      notifications: {
        setupSlack: {
          bg: background.secondary,
        },
      },
    },
  },
  docMention: {
    bg: Color.Grey100,
    border: Color.Grey300,
    input: {
      bg: Color.Grey100,
    },
  },
  appPreview: {
    border: border.greyLight,
  },
  emojiPicker: {
    theme: 'light',
  },
  docPanelSeparator: {
    color: Color.Grey300,
  },
  mark: {
    bg: Color.Yellow300,
  },
  helpCenter: {
    button: {
      bg: Color.White,
    },
  },

  // We keep this here so we can merge the sidebar colors in the getTheme function
  sidebarOverride: {},

  customers: {
    tab: {
      bgHover: Color.Grey200,
    },
    badge: {
      bg: Color.Grey200,
      bgHover: Color.Grey300,
    },
    link: {
      text: Color.Black,
      textHover: Color.Black,
      bg: Color.White,
      bgHover: Color.Grey100,
      border: rgba(Color.Black, 0.06),
    },
  },

  ui: {
    info: {
      default: {
        title: Color.Grey800,
        text: Color.Grey700,
        bg: Color.Grey100,
        bgHover: Color.Grey200,
      },
      danger: {
        title: Color.Red800,
        text: Color.Red700,
        bg: Color.Red100,
        bgHover: Color.Red100,
      },
    },
  },
  logo: {
    main: '#2E48ED',
    alt: Color.Black,
  },
  popup: {
    bg: Color.White,
    separator: rgba(Color.Black, 0.1),
  },
  billing: {
    section: {
      bg: Color.White,
      border: 'rgba(0, 0, 0, 0.06)',
      shadow: 'rgba(0, 0, 0, 0.05)',
      separator: Color.Grey200,
    },
    history: {
      bg: Color.Grey100,
    },
    plan: {
      free: {
        bg: background.greenlight,
        fg: text.primary,
        color: Color.Green800,
      },
      standard: {
        bg: Color.Pink400,
        bgLight: Color.Pink200,
        fg: text.primary,
        color: Color.Pink700,
      },
      business: {
        bg: Color.Blue200,
        fg: text.primary,
        color: Color.Blue800,
      },
    },
    discount: {
      annual: {
        bg: Color.Green200,
        color: Color.Green800,
        border: Color.Green800,
      },
    },
  },
  onboarding: {
    background: {
      body: background.primary,
      aside: Color.Grey200,
      backdrop: 'rgba(191, 191, 191, 0.68)',
      docPanel: background.primary,
    },
    integration: {
      circle: Color.Blue300,
    },
  },
  lightOnboarding: {
    layout: {
      headline: {
        color: Color.Blue600,
      },
      card: {
        bg: Color.White,
      },
    },
    background: {
      body: Color.Grey300,
      main: background.primary,
      aside: Color.Grey100,
    },
    themePreview: {
      light: {
        bg: Color.Grey200,
        main: Color.Grey100,
        bar: Color.White,
        outline: Color.Grey200,
      },
      dark: {
        bg: Color.Grey800,
        main: Color.Grey900,
        bar: Color.Black,
        outline: 'transparent',
      },
    },
    asideApp: {
      gradient: 'linear-gradient(164.2deg,rgba(36,36,36,0) 37.74%,rgb(255 255 255) 77.11%)',
      container: {
        border: border.primary,
      },
      sidebar: {
        bg: Color.White,
      },
      sidebarItem: {
        color: Color.Grey500,
        bg: Color.Grey300,
      },
      main: {
        bg: Color.Grey100,
      },
      mainHeader: {
        bg: Color.Grey200,
      },
      mainCard: {
        bg: Color.White,
      },
    },
    integration: {
      circle: Color.Blue300,
    },
    doneLoader: {
      color: Color.Grey300,
    },
  },
  home: {
    separator: Color.Grey300,
    card: {
      bg: Color.White,
      bgHover: lighten(0.01, Color.Grey100),
    },
    search: {
      bg: Color.Grey200,
      text: Color.Grey700,
    },
    cmdk: {
      bg: Color.Grey300,
      text: Color.Grey500,
    },
  },
  inbox: {
    sources: {
      bg: Color.Grey200,
    },
    zero: {
      item: {
        text: background.disabled,
        description: background.tertiary,
        bg: Color.White,
      },
    },
  },
  chrome: {
    skeleton: {
      bg: Color.White,
      title: background.tertiary,
    },
  },
  components: {
    EditorIntegrationCard: {
      border: Color.Grey300,
    },
    FeedbackStatus: {
      [StatusCategory.NotStarted]: {
        main: Color.Grey400,
        text: Color.Grey900,
        bg: Color.Grey100,
        bgHover: darken(0.05, Color.Grey100),
        bgActive: darken(0.1, Color.Grey100),
      },
      [StatusCategory.Started]: {
        main: Color.Blue800,
        text: Color.Blue800,
        bg: Color.Blue200,
        bgHover: darken(0.05, Color.Blue200),
        bgActive: darken(0.1, Color.Blue200),
      },
      [StatusCategory.Completed]: {
        main: Color.Green800,
        text: Color.Green800,
        bg: '#E1F3E5',
        bgHover: darken(0.05, '#E1F3E5'),
        bgActive: darken(0.1, '#E1F3E5'),
      },
      [StatusCategory.Canceled]: {
        main: Color.Red800,
        text: Color.Red800,
        bg: Color.OrangeRed200,
        bgHover: darken(0.05, Color.OrangeRed200),
        bgActive: darken(0.1, Color.OrangeRed200),
      },
    },
    Toaster: {
      bg: Color.White,
      border: rgba(0, 0, 0, 0.06),
      color: Color.Grey700,
      light: Color.Grey400,
    },
    ProductTourModal: {
      bg: Color.Blue500,
      text: Color.White,
    },
    HelpMeWriting: {
      bg: Color.Blue100,
    },
  },
};

// We don't want to put this directly into the theme above
// to avoid other themes from inheriting those values
export const sidebarColors: DeepPartial<typeof colors> = {
  scrollbar: {
    color: rgba(LegacyColor.Grey, 0.2),
    colorHover: rgba(LegacyColor.GreyLight2, 0.2),
  },
  dropdown: {
    border: Color.Grey800,
    bg: Color.Grey850,
    color: Color.White,
    sep: Color.Grey800,
  },
  selectItem: {
    color: Color.White,
    bgHover: Color.Grey800,
  },
  selectItemDanger: {
    colorHover: Color.Red300,
    bgHover: Color.Red800,
  },
  tooltip: {
    bg: Color.Grey700,
    color: Color.White,
    textLight: Color.Grey400,
    shortcut: {
      bg: Color.Grey800,
      text: Color.Grey400,
    },
  },
};

export const nuance: ColorNuance = {
  a: {
    bg: '#1557FF1A',
    br: '#1557FF26',
    fg: '#1557FF',
    main: '#1557FF',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  b: {
    bg: '#1681FF1A',
    br: '#1681FF26',
    fg: '#1681FF',
    main: '#1681FF',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  c: {
    bg: '#13CDA01A',
    br: '#0DC91326',
    fg: '#00BA8D',
    main: '#00BA8D',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  d: {
    bg: '#13CD661A',
    br: '#13CD6626',
    fg: '#0BBC5A',
    main: '#0BBC5A',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  e: {
    bg: '#BE06FF1A',
    br: '#BE06FF26',
    fg: '#BE06FF',
    main: '#BE06FF',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  f: {
    bg: '#9206FF1A',
    br: '#9206FF26',
    fg: '#9206FF',
    main: '#9206FF',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  g: {
    bg: '#D91BF81A',
    br: '#D91BF826',
    fg: '#D91BF8',
    main: '#D91BF8',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  h: {
    bg: '#611BF81A',
    br: '#611BF826',
    fg: '#611BF8',
    main: '#611BF8',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  i: {
    bg: '#F15D3C1A',
    br: '#F15D3C26',
    fg: '#FF512B',
    main: '#FF512B',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  j: {
    bg: '#FBAA301A',
    br: '#FBAA3026',
    fg: '#FF9B05',
    main: '#FF9B05',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  k: {
    bg: '#FBCE301A',
    br: '#BA930926',
    fg: '#E5A500',
    main: '#E5A500',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
  l: {
    bg: '#F13D3C1A',
    br: '#F13D3C26',
    fg: '#FC3331',
    main: '#FC3331',
    textHighlight: '',
    markHighlight: '',
    markHighlightBorder: '',
  },
};

for (const key of Object.keys(nuance)) {
  const color = key as ColorApi;
  nuance[color].textHighlight = opacify(0.15, nuance[color].bg);
  nuance[color].markHighlight = opacify(0.12, nuance[color].bg);
  nuance[color].markHighlightBorder = opacify(0.18, nuance[color].bg);
}
